import React, { useMemo } from 'react';
import ShipIcon from '@atlaskit/icon/glyph/ship';
import Lozenge from '@atlaskit/lozenge';
import { Box, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ToolTip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { CardId } from '../../../../../model/types';
import { useBacklogSelector } from '../../../../../state';
import { cardVersionsSelector } from '../../../../../state/selectors/cards';
import { ScreenSize } from '../../../hooks/common/constants';
import { VersionIconWrapper, VersionIconNumber } from '../../styled';
import { messages } from './messages';

export interface Props {
	cardId: CardId;
	backlogContainerSize: ScreenSize | undefined;
}

export function VersionsField({ cardId, backlogContainerSize }: Props) {
	const { formatMessage } = useIntl();
	const versions = useBacklogSelector((state) => cardVersionsSelector(state, cardId));

	const tooltipText = useMemo(() => versions.map((version) => version.name).join(', '), [versions]);

	const text = useMemo(
		() =>
			versions.length === 1
				? versions[0].name
				: formatMessage(messages.text, { count: versions.length }),
		[formatMessage, versions],
	);

	if (!versions.length) {
		return null;
	}

	return (
		<Box xcss={containerStyles}>
			<ToolTip content={tooltipText}>
				{backlogContainerSize === ScreenSize.EXTRA_SMALL ||
				backlogContainerSize === ScreenSize.SMALL ? (
					<VersionIconWrapper>
						<ShipIcon
							primaryColor={token('color.text.subtlest', colors.N200)}
							size="small"
							label="version icon"
						/>
						<VersionIconNumber>{versions.length}</VersionIconNumber>
					</VersionIconWrapper>
				) : (
					<Lozenge>{text}</Lozenge>
				)}
			</ToolTip>
		</Box>
	);
}

const containerStyles = xcss({
	maxWidth: '100%',
	pointerEvents: 'all',
});
